import React, { useState, useEffect } from 'react';
import { Theme } from './types';

interface ImageLoadingAnimationProps {
  theme: Theme;
  message?: string;
}

const ImageLoadingAnimation: React.FC<ImageLoadingAnimationProps> = ({ theme, message = "Loading image..." }) => {
  const [rotation, setRotation] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(function animate() {
      setRotation(r => (r + 2) % 360);
      setProgress(p => (p + 0.5) % 100);
      requestAnimationFrame(animate);
    });

    return () => cancelAnimationFrame(animationFrame);
  }, []);

  return (
    <div style={{
      textAlign: 'center',
      padding: '40px 20px',
      color: theme === 'light' ? '#666666' : '#999999',
      backgroundColor: theme === 'light' ? '#F8F9FA' : '#1A1A1A',
      borderRadius: '8px',
      border: `1px solid ${theme === 'light' ? '#E5E7EB' : '#2D2D2D'}`
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px'
      }}>
        <div style={{
          position: 'relative',
          width: '80px',
          height: '80px'
        }}>
          {/* Background circle */}
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            border: `2px solid ${theme === 'light' ? '#E5E7EB' : '#2D2D2D'}`,
            borderRadius: '50%'
          }} />
          
          {/* Progress circle */}
          <svg
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              transform: 'rotate(-90deg)'
            }}
            viewBox="0 0 32 32"
          >
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke={theme === 'light' ? '#E5E7EB' : '#2D2D2D'}
              strokeWidth="2"
            />
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#10A37F"
              strokeWidth="2"
              strokeDasharray={`${progress} 100`}
              strokeLinecap="round"
              style={{
                transition: 'stroke-dasharray 0.3s ease'
              }}
            />
          </svg>

          {/* Spinning rings */}
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${60 - i * 15}px`,
                height: `${60 - i * 15}px`,
                border: `2px solid ${theme === 'light' ? '#E5E7EB' : '#2D2D2D'}`,
                borderTopColor: '#10A37F',
                borderRadius: '50%',
                transform: `translate(-50%, -50%) rotate(${rotation + i * 30}deg)`,
                transition: 'transform 0.016s linear'
              }}
            />
          ))}

          {/* Center dot */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '12px',
            height: '12px',
            backgroundColor: '#10A37F',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 0 10px rgba(16, 163, 127, 0.3)'
          }} />
        </div>

        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 500,
            color: theme === 'light' ? '#374151' : '#D1D5DB'
          }}>
            {message}
          </div>
          <div style={{
            fontSize: '12px',
            color: theme === 'light' ? '#9CA3AF' : '#6B7280',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: '6px',
                  height: '6px',
                  backgroundColor: '#10A37F',
                  borderRadius: '50%',
                  opacity: Math.cos((rotation + i * 120) * Math.PI / 180) * 0.5 + 0.5,
                  transition: 'opacity 0.016s ease'
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageLoadingAnimation;